import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FiChevronRight, FiX, FiUser } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import LanguageMenu from './LanguageMenu';
import { isLanguageCategory } from '../../utils/languageNavigation';

/**
 * Amazon-style left-side slide-in drawer.
 *
 * Props:
 *   open                  - boolean, controls visibility / slide
 *   onClose               - () => void, called when the user dismisses
 *   categories            - array of { id, name } from the API
 *   highlightedCategory   - string | null, name of the category to highlight
 *   highlightedSort       - string | null, active trending filter
 */
const CategoryDrawer = ({
  open,
  onClose,
  categories,
  highlightedCategory,
  highlightedSort,
}) => {
  const { isAuthenticated, user } = useAuth();
  const highlightedRef = useRef(null);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // Lock body scroll while the drawer is open
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Scroll the highlighted item into view once the drawer opens
  useEffect(() => {
    if (open && highlightedRef.current) {
      // small delay so the slide-in transition finishes first
      const t = setTimeout(() => {
        highlightedRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }, 250);
      return () => clearTimeout(t);
    }
  }, [open, highlightedCategory, highlightedSort]);

  const greeting = isAuthenticated
    ? `Hello, ${user?.name?.split(' ')[0] || 'there'}`
    : 'Hello, sign in';

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/50 z-[60] transition-opacity duration-300 ${
          open ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <aside
        className={`fixed top-0 left-0 h-full w-[85%] max-w-sm bg-white z-[70] shadow-none md:shadow-2xl flex flex-col transform transition-transform duration-300 ease-out ${
          open ? 'translate-x-0' : '-translate-x-full'
        }`}
        role="dialog"
        aria-modal="true"
        aria-hidden={!open}
        aria-label="Main menu"
      >
        {/* Dark header: greeting + close */}
        <header
          className="flex items-center justify-between px-4 py-3 text-white shrink-0"
          style={{ backgroundColor: '#2D3142' }}
        >
          <Link
            to={isAuthenticated ? '/profile' : '/login'}
            onClick={onClose}
            className="flex items-center gap-2 font-bold text-base"
          >
            <span className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center">
              <FiUser className="text-base" />
            </span>
            {greeting}
          </Link>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            className="p-1.5 rounded hover:bg-white/10 transition"
          >
            <FiX className="text-xl" />
          </button>
        </header>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto">
          {/* Trending */}
          <section>
            <h3 className="px-4 pt-4 pb-2 text-base font-bold text-black">Trending</h3>
            <ul className="list-none">
              <li>
                <Link
                  to="/#bestsellers"
                  onClick={onClose}
                  aria-current={highlightedSort === 'bestsellers' ? 'page' : undefined}
                  className={`flex items-center justify-between px-4 py-2.5 text-sm border-l-4 ${
                    highlightedSort === 'bestsellers'
                      ? 'bg-amber-50 text-orange-600 font-semibold border-orange-500'
                      : 'text-black border-transparent hover:bg-gray-100'
                  }`}
                >
                  Bestsellers
                </Link>
              </li>
              <li>
                <Link
                  to="/#new-releases"
                  onClick={onClose}
                  aria-current={highlightedSort === 'new-releases' ? 'page' : undefined}
                  className={`flex items-center justify-between px-4 py-2.5 text-sm border-l-4 ${
                    highlightedSort === 'new-releases'
                      ? 'bg-amber-50 text-orange-600 font-semibold border-orange-500'
                      : 'text-black border-transparent hover:bg-gray-100'
                  }`}
                >
                  New Releases
                </Link>
              </li>
            </ul>
          </section>

          <hr className="my-2 border-gray-200" />

          {/* Shop by Category */}
          <section>
            <h3 className="px-4 pt-3 pb-2 text-base font-bold text-black">
              Shop by Category
            </h3>
            {categories.length === 0 ? (
              <p className="px-4 py-3 text-sm text-black">Loading categories...</p>
            ) : (
              <ul className="list-none">
                {categories.map((category) => {
                  const isActive = highlightedCategory === category.name;
                  return (
                    <li key={category.id} ref={isActive ? highlightedRef : null}>
                      {isLanguageCategory(category.name) ? (
                        open && <LanguageMenu label={category.name} onSelect={onClose} />
                      ) : (
                        <Link
                          to={`/shop?category=${encodeURIComponent(category.name).replace(/%20/g, '+')}`}
                          onClick={onClose}
                          aria-current={isActive ? 'page' : undefined}
                          className={`flex items-center justify-between px-4 py-2.5 text-sm border-l-4 ${
                            isActive
                              ? 'bg-amber-50 text-orange-600 font-semibold border-orange-500'
                              : 'text-black border-transparent hover:bg-gray-100'
                          }`}
                        >
                          <span className="truncate">{category.name}</span>
                          <FiChevronRight
                            className={`shrink-0 ${isActive ? 'text-orange-500' : 'text-black'}`}
                          />
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <hr className="my-2 border-gray-200" />

          {/* Help & Settings */}
          <section>
            <h3 className="px-4 pt-3 pb-2 text-base font-bold text-black">
              Help &amp; Settings
            </h3>
            <ul className="list-none pb-4">
              <li>
                <Link
                  to={isAuthenticated ? '/profile' : '/login'}
                  onClick={onClose}
                  className="flex items-center justify-between px-4 py-2.5 text-sm text-black hover:bg-gray-100"
                >
                  Your Account
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  onClick={onClose}
                  className="flex items-center justify-between px-4 py-2.5 text-sm text-black hover:bg-gray-100"
                >
                  Customer Service
                </Link>
              </li>
              {!isAuthenticated && (
                <li>
                  <Link
                    to="/login"
                    onClick={onClose}
                    className="flex items-center justify-between px-4 py-2.5 text-sm text-black hover:bg-gray-100"
                  >
                    Sign In
                  </Link>
                </li>
              )}
            </ul>
          </section>
        </div>
      </aside>
    </>
  );
};

export default CategoryDrawer;
